import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useSearch, useLocation } from '@tanstack/react-router';
import { useAuth } from '@/contexts/AuthContext';
import {
  useSubscription,
  useStoredPlan,
  isSubscriptionActive,
  requiresPayment,
  isCanceled,
} from '@/hooks/useSubscription';
import { Loader2 } from 'lucide-react';

/**
 * AuthGate handles subscription-based routing for protected routes.
 *
 * Routing logic:
 * - No auth → redirect to /
 * - No subscription → /app/plan-selection
 * - Pending status → /app/complete-payment
 * - Canceled status → /app/plan-selection
 * - Active/trialing/past_due/internal → check ?plan= param
 *   - Different plan → /app/plan-change
 *   - Same or no param → /app/dashboard
 */
export function AuthGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, initialized } = useAuth();
  const { subscription, isLoading: subLoading, invalidate } = useSubscription();
  const { storedPlan, clearStoredPlan } = useStoredPlan();

  // Get plan from URL query params
  const search = useSearch({ strict: false }) as { plan?: string; checkout?: string; portal?: string };
  const planParam = search?.plan || storedPlan;
  const checkoutSuccess = search?.checkout === 'success';
  const portalReturn = search?.portal === 'return';

  const currentPath = location.pathname;

  // Track if we've handled Stripe return (to invalidate cache once)
  const handledStripeReturn = useRef(false);
  // Track timeout for subscription loading
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  // Handle return from Stripe: invalidate cache to force fresh fetch
  // Using invalidate() instead of refetch() because refetch() is a no-op if query is disabled
  useEffect(() => {
    if ((checkoutSuccess || portalReturn) && !handledStripeReturn.current) {
      handledStripeReturn.current = true;
      invalidate();
    }
  }, [checkoutSuccess, portalReturn, invalidate]);

  // Timeout for subscription loading to prevent infinite spinner
  useEffect(() => {
    if (user && subLoading && !loadingTimedOut) {
      const timer = setTimeout(() => {
        setLoadingTimedOut(true);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [user, subLoading, loadingTimedOut]);

  // Reset timeout flag when subscription loads successfully
  useEffect(() => {
    if (!subLoading && loadingTimedOut) {
      setLoadingTimedOut(false);
    }
  }, [subLoading, loadingTimedOut]);

  // Wait for subscription to load (with timeout protection)
  const waitingForSubscription = user && subLoading && !loadingTimedOut;
  const isLoading = authLoading || !initialized || waitingForSubscription;

  useEffect(() => {
    // Wait for auth to initialize
    if (!initialized) return;

    // Not authenticated - redirect to landing
    if (!user) {
      navigate({ to: '/' });
      return;
    }

    // Wait for subscription data to load (unless timed out)
    if (subLoading && !loadingTimedOut) return;

    // No subscription - allow plan selection and complete payment pages
    if (!subscription) {
      if (currentPath !== '/app/plan-selection' && currentPath !== '/app/complete-payment') {
        navigate({ to: '/app/plan-selection' });
      }
      return;
    }

    // Subscription exists - check status
    const { status, plan_type } = subscription;

    // If user just returned from Stripe (checkout or portal), let routing happen based on fresh data
    // Don't skip redirects - we want to route them to the correct page based on updated status
    // Pending payment - go to complete payment (if not already there)
    if (requiresPayment(status)) {
      if (currentPath !== '/app/complete-payment') {
        navigate({ to: '/app/complete-payment' });
      }
      return;
    }

    // Canceled - go to plan selection (if not already there)
    if (isCanceled(status)) {
      if (currentPath !== '/app/plan-selection') {
        navigate({ to: '/app/plan-selection' });
      }
      return;
    }

    // Active subscription - check if trying to change plan
    if (isSubscriptionActive(status)) {
      // Check if user clicked a different plan from marketing
      if (
        planParam &&
        ['solo', 'pro', 'team'].includes(planParam) &&
        planParam !== plan_type
      ) {
        if (currentPath !== '/app/plan-change') {
          navigate({ to: '/app/plan-change', search: { plan: planParam } });
        }
        return;
      }

      // Clear stored plan once we've checked it
      if (storedPlan) {
        clearStoredPlan();
      }

      // Stay on current route (dashboard or wherever they navigated to)
      // The Outlet will render the child route
    }
  }, [
    initialized,
    user,
    subscription,
    subLoading,
    loadingTimedOut,
    planParam,
    storedPlan,
    navigate,
    clearStoredPlan,
    currentPath,
  ]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-sapling-primary-100" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - don't render anything, useEffect will redirect
  if (!user) {
    return null;
  }

  // No subscription - allow plan-selection and complete-payment pages, block everything else
  if (!subscription) {
    if (currentPath !== '/app/plan-selection' && currentPath !== '/app/complete-payment') {
      return null; // Block, useEffect will redirect to plan selection
    }
    // Allow rendering plan-selection or complete-payment page
    return <Outlet />;
  }

  // Check subscription status - allow access to appropriate pages based on status
  // (unless they just came from successful checkout)
  if (!checkoutSuccess) {
    // Pending status - only allow /app/complete-payment
    if (requiresPayment(subscription.status)) {
      if (currentPath !== '/app/complete-payment') {
        return null; // Block, useEffect will redirect
      }
      // Allow rendering complete-payment page
    }

    // Canceled status - only allow /app/plan-selection
    if (isCanceled(subscription.status)) {
      if (currentPath !== '/app/plan-selection') {
        return null; // Block, useEffect will redirect
      }
      // Allow rendering plan-selection page
    }
  }

  // User has valid subscription or is on the correct page for their status - render child routes
  return <Outlet />;
}
